import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, KeyRound } from 'lucide-react';
import { PLATFORM_SPECS } from '../../../shared/platforms';
import { normalizeSiteUrl } from '../../../shared/utils';
import type { SocialAccount } from '../../../shared/types';
import { Alert, Button, Dialog, Field, Input, useToast } from '../ui';
import { api, ApiError } from '../../lib/api';
import { useWorkspaceStore } from '../../stores/workspace';

export interface WordPressConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: (account: SocialAccount) => void;
}

const SPEC = PLATFORM_SPECS.wordpress;

/**
 * WordPress has no OAuth — a site URL plus a WordPress Application Password
 * (Users → Profile → Application Passwords) authenticates as Basic auth.
 */
export function WordPressConnectDialog({ open, onOpenChange, onConnected }: WordPressConnectDialogProps) {
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [siteUrl, setSiteUrl] = useState('');
  const [username, setUsername] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const reset = () => {
    setSiteUrl('');
    setUsername('');
    setAppPassword('');
    setFieldError(null);
  };

  const connect = useMutation({
    mutationFn: () =>
      api.accounts.connectWordPress({
        siteUrl: normalizeSiteUrl(siteUrl) ?? siteUrl.trim(),
        username: username.trim(),
        appPassword: appPassword.trim(),
      }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'accounts'] });
      toast({ title: `${response.account.displayName} connected`, tone: 'success' });
      onConnected(response.account);
      reset();
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: 'Could not connect that site',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const error = connect.error instanceof ApiError ? connect.error : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!connect.isPending) {
          onOpenChange(next);
          if (!next) reset();
        }
      }}
      title="Connect a WordPress site"
      description="Uses an Application Password — never your admin login."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={connect.isPending}>
            Cancel
          </Button>
          <Button
            loading={connect.isPending}
            leftIcon={<KeyRound className="size-4" />}
            onClick={() => {
              if (!normalizeSiteUrl(siteUrl)) {
                setFieldError('Enter a valid site URL, e.g. https://example.com');
                return;
              }
              if (!username.trim() || !appPassword.trim()) {
                setFieldError('Username and application password are both required.');
                return;
              }
              setFieldError(null);
              connect.mutate();
            }}
          >
            Connect site
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {(fieldError || error) && (
          <Alert tone="danger" title="Could not connect">
            {fieldError ?? error?.message}
          </Alert>
        )}
        <Field label="Site URL" htmlFor="wp-site-url" required>
          <Input
            id="wp-site-url"
            type="url"
            placeholder="https://example.com"
            autoFocus
            value={siteUrl}
            onChange={(event) => setSiteUrl(event.target.value)}
          />
        </Field>
        <Field label="Username" htmlFor="wp-username" required>
          <Input
            id="wp-username"
            placeholder="your WordPress login"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
        </Field>
        <Field
          label="Application password"
          htmlFor="wp-app-password"
          required
          hint="Generate one under Users → Profile → Application Passwords on the site itself."
        >
          <Input
            id="wp-app-password"
            type="password"
            autoComplete="off"
            placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
            value={appPassword}
            onChange={(event) => setAppPassword(event.target.value)}
          />
        </Field>
        <a
          href={SPEC.docsUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
        >
          <ExternalLink className="size-3.5" />
          How Application Passwords work
        </a>
      </div>
    </Dialog>
  );
}
